import { Grid, Paper, Box, Typography } from '@mui/material'
import {
    Inbox,
    Visibility,
    Reply,
    Business,
    CheckCircle,
    Archive,
    AllInbox,
    Lock,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from '@i18n'
import { useSubmissionStore } from '@store/submissionStore'
import { useAuthStore } from '@store/authStore'

const StatsCards = ({ stats }) => {
    const { t } = useTranslation()
    const navigate = useNavigate()
    const { setFilter, resetFilters } = useSubmissionStore()
    const { user } = useAuthStore()

    const isFormManager = user?.user_type === 'form_manager'

    const allCards = [
        {
            key: 'total',
            label: t('stats.total'),
            value: stats?.total || 0,
            icon: <AllInbox />,
            color: '#1a3a6b',
            bgColor: 'rgba(102, 126, 234, 0.1)',
            filter: null,
            showForManager: true,
        },
        {
            key: 'new',
            label: t('stats.new'),
            value: stats?.new_count || 0,
            icon: <Inbox />,
            color: '#3b82f6',
            bgColor: 'rgba(59, 130, 246, 0.1)',
            filter: 'new',
            showForManager: true,
        },
        {
            key: 'read',
            label: t('stats.read'),
            value: stats?.read_count || 0,
            icon: <Visibility />,
            color: '#ef4444',
            bgColor: 'rgba(239, 68, 68, 0.1)',
            filter: 'read',
            showForManager: true,
        },
        {
            key: 'replied',
            label: t('stats.replied'),
            value: stats?.replied_count || 0,
            icon: <Reply />,
            color: '#10b981',
            bgColor: 'rgba(16, 185, 129, 0.1)',
            filter: 'replied',
            showForManager: true,
        },
        {
            key: 'pending_department',
            label: t('stats.pendingDepartment'),
            value: stats?.pending_department_count || 0,
            icon: <Business />,
            color: '#f59e0b',
            bgColor: 'rgba(245, 158, 11, 0.1)',
            filter: 'pending_department',
            showForManager: false,
        },
        {
            key: 'department_replied',
            label: t('stats.departmentReplied'),
            value: stats?.department_replied_count || 0,
            icon: <CheckCircle />,
            color: '#06b6d4',
            bgColor: 'rgba(6, 182, 212, 0.1)',
            filter: 'department_replied',
            showForManager: false,
        },
        {
            key: 'closed',
            label: t('stats.closed'),
            value: stats?.closed_count || 0,
            icon: <Lock />,
            color: '#475569',
            bgColor: 'rgba(71, 85, 105, 0.1)',
            filter: 'closed',
            showForManager: true,
        },
        {
            key: 'archived',
            label: t('stats.archived'),
            value: stats?.archived_count || 0,
            icon: <Archive />,
            color: '#64748b',
            bgColor: 'rgba(100, 116, 139, 0.1)',
            filter: 'archived',
            showForManager: true,
        },
    ]

    // Filter cards for form_manager
    const cards = isFormManager ? allCards.filter(c => c.showForManager) : allCards

    const handleCardClick = (filter) => {
        if (filter === 'archived') {
            setFilter('archived', true)
            setFilter('status', null)
        } else if (filter === null) {
            resetFilters()
        } else {
            setFilter('archived', false)
            setFilter('status', filter)
        }
        navigate('/submissions')
    }

    return (
        <Grid container spacing={2}>
            {cards.map((card) => (
                <Grid item xs={12} sm={6} md={4} lg={3} xl key={card.key}>
                    <Paper
                        onClick={() => handleCardClick(card.filter)}
                        sx={{
                            p: 3,
                            cursor: 'pointer',
                            borderRadius: 3,
                            transition: 'all 0.2s ease',
                            '&:hover': {
                                transform: 'translateY(-4px)',
                                boxShadow: 4,
                            },
                        }}
                    >
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'flex-start',
                                justifyContent: 'space-between',
                            }}
                        >
                            <Box>
                                <Typography
                                    variant="h3"
                                    fontWeight={700}
                                    sx={{ color: card.color }}
                                >
                                    {card.value}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    color="text.secondary"
                                    sx={{ mt: 0.5 }}
                                >
                                    {card.label}
                                </Typography>
                            </Box>
                            <Box
                                sx={{
                                    p: 1.5,
                                    borderRadius: 2,
                                    bgcolor: card.bgColor,
                                    color: card.color,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}
                            >
                                {card.icon}
                            </Box>
                        </Box>
                    </Paper>
                </Grid>
            ))}
        </Grid>
    )
}

export default StatsCards
